import { Observable, EMPTY, defer } from "rxjs"
import { Router } from "../../../server/router"
import type { EpicDependencies } from "../../rxjs/configureEpics"
import { CLOSE_ROLE_FORM_CLICKED, DELETE_ROLE_CLICKED, ROLE_FORM_SUBMITTED } from "./actions"
import {
    accessLevelCreated,
    accessLevelDeleted,
    accessLevelUpdated,
    permissionCreated,
    permissionDeleted,
} from "../../data/actions"
import { composePermission } from "./utils"
import { getAccesses } from "../../data/selectors"
import { logger } from "../../../utils/loggerWrap"
import { ofType, StateObservable } from "redux-observable"
import { catchError, switchMap } from "rxjs/operators"
import { Action, StateObject } from "../../redux/types"
import { createEventRequest, requestEvent } from "../../../events/utils"
import { EventType } from "../../../events/types"
import { getHttpHeaders, getSelectedAccountId } from "../../auth/selectors"
import { NotificationType, openNotification } from "../../uiKit/Notification/Notification"
import { getAccessLevelSavePayload } from "./accessLevelSavePayload"

const isAccessLevelEventFlowV2Enabled = async (): Promise<boolean> => {
    try {
        const response = await fetch("/manage-api/feature-flags", { credentials: "include" })
        if (!response.ok) {
            return false
        }
        const data = await response.json()
        logger.info("rolesEpics - fetched feature flags", !!data?.["accessLevelEventFlowV2"])
        return !!data?.["accessLevelEventFlowV2"]
    } catch (error) {
        logger.error("rolesEpics - failed to fetch feature flags", null, error)
        return false
    }
}

export const roleFormSubmittedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ROLE_FORM_SUBMITTED),
        switchMap((action: Action) =>
            defer(() => isAccessLevelEventFlowV2Enabled()).pipe(
                switchMap(isEnabled => {
                    if (!isEnabled) {
                        logger.info("roleFormSubmittedEpics - form submitted", {
                            data: action.payload,
                        })
                        if (!action.payload.oldValue) {
                            const newVal = action.payload.newValue
                            const arrayOfKeys = Object.keys(newVal)
                            const newAccess = {
                                id: deps.idgen.next(),
                                name: newVal.roleName,
                                permissions: [],
                                deleted: false,
                            }
                            const permissionActions = arrayOfKeys
                                .filter(key => key !== "roleName")
                                .map(key => {
                                    if (newVal[key]) {
                                        const newPermission = composePermission(newAccess.id, key, deps.idgen)
                                        return permissionCreated(newPermission)
                                    }

                                    return null
                                })
                                .filter(a => a)
                            return [accessLevelCreated(newAccess), ...permissionActions]
                        }

                        const oldVal = action.payload.oldValue
                        const newVal = action.payload.newValue
                        const arrayOfKeys = Object.keys(newVal)
                        const permissionActions = arrayOfKeys
                            .filter(key => key !== "roleName")
                            .map(key => {
                                // If new value is true, check if it is in old. If yes, no action needed. If no, create new.
                                if (newVal[key]) {
                                    const isInOldId = oldVal[key].id
                                    return isInOldId
                                        ? null
                                        : permissionCreated(
                                              composePermission(action.payload.oldValue.accessLevelId, key, deps.idgen)
                                          )
                                } else {
                                    // Value is False
                                    const isInOldId = oldVal[key].id
                                    return isInOldId
                                        ? permissionDeleted(isInOldId, action.payload.oldValue.accessLevelId)
                                        : null
                                }
                            })
                            .filter(a => a)
                        return [
                            accessLevelUpdated({
                                id: oldVal.accessLevelId,
                                name: newVal.roleName,
                                permissions: [],
                                deleted: false,
                            }),
                            ...permissionActions,
                        ]
                    }

                    const { payload, actions } = getAccessLevelSavePayload(action.payload, deps.idgen)
                    logger.info("roleFormSubmittedEpics - AccessLevelSaveRequested", {
                        data: payload,
                    })

                    return defer(() =>
                        requestEvent(
                            getHttpHeaders(deps.getState()),
                            createEventRequest(
                                EventType.AccessLevelSaveRequested,
                                {
                                    accountId: getSelectedAccountId(deps.getState()) as string,
                                    correlationId: deps.idgen.next(),
                                },
                                payload
                            )
                        )
                    ).pipe(
                        switchMap(() => actions),
                        catchError(error => {
                            logger.error("roleFormSubmittedEpics - AccessLevelSaveRequested failed", null, error)
                            openNotification(
                                NotificationType.Error,
                                "Role could not be saved",
                                "Please try again later."
                            )
                            return EMPTY
                        })
                    )
                })
            )
        )
    )

export const roleDeleteClickedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(DELETE_ROLE_CLICKED),
        switchMap((action: Action) =>
            defer(() => isAccessLevelEventFlowV2Enabled()).pipe(
                switchMap(isEnabled => {
                    logger.info("roleDeleteClickedEpics - delete clicked", {
                        data: action.payload,
                    })
                    const allAccesses = getAccesses(deps.getState())
                    const findAccess = allAccesses.find(a => a.id === action.payload.id)

                    if (!findAccess) {
                        return []
                    }

                    if (!isEnabled) {
                        return [
                            ...findAccess.permissions.map(p => permissionDeleted(p.id, action.payload.id)),
                            accessLevelDeleted(action.payload.id),
                        ]
                    }

                    return defer(() =>
                        requestEvent(
                            getHttpHeaders(deps.getState()),
                            createEventRequest(
                                EventType.AccessLevelDeleteRequested,
                                {
                                    accountId: getSelectedAccountId(deps.getState()) as string,
                                    correlationId: deps.idgen.next(),
                                },
                                { id: action.payload.id }
                            )
                        )
                    ).pipe(
                        switchMap(() => [
                            ...findAccess.permissions.map(p => permissionDeleted(p.id, action.payload.id, true)),
                            accessLevelDeleted(action.payload.id, true),
                        ]),
                        catchError(error => {
                            logger.error("roleDeleteClickedEpics - AccessLevelDeleteRequested failed", null, error)
                            openNotification(
                                NotificationType.Error,
                                "Role could not be deleted",
                                "Please try again later."
                            )
                            return EMPTY
                        })
                    )
                })
            )
        )
    )

export const roleSubmittedRouterEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ROLE_FORM_SUBMITTED, CLOSE_ROLE_FORM_CLICKED, DELETE_ROLE_CLICKED),
        switchMap(() => {
            Router.push("/employees/roles")
            return EMPTY
        })
    )

export default [roleDeleteClickedEpics, roleSubmittedRouterEpics, roleFormSubmittedEpics]
